import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  GraduationCap, 
  Layers, 
  BookOpen, 
  Compass, 
  Award, 
  Sparkles, 
  PhoneCall, 
  Save, 
  Clock, 
  Upload, 
  Image as ImageIcon,
  Plus,
  Trash2,
  Eye,
  X
} from 'lucide-react';
import { SECONDARY_DEFAULTS } from '../../../data/secondaryDefaults';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const SUB_TABS = [
  { id: 'hero', title: 'Hero & Stats', icon: GraduationCap },
  { id: 'overview', title: 'Overview & Highlights', icon: Layers },
  { id: 'pathways', title: 'Academic Pathways', icon: BookOpen },
  { id: 'tracks', title: 'Subject Tracks', icon: Compass },
  { id: 'support', title: 'Support & Counseling', icon: Sparkles },
  { id: 'achievements', title: 'Achievements & Opportunities', icon: Award },
  { id: 'cta', title: 'Call To Action', icon: PhoneCall }
];

export default function SecondaryPageForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [activeTab, setActiveTab] = useState('hero');
  const [formData, setFormData] = useState(SECONDARY_DEFAULTS);
  const [originalData, setOriginalData] = useState(SECONDARY_DEFAULTS);
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [sectionId, setSectionId] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[SecondaryPageForm] Media fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=secondary`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'secondary' || s.section_type === 'secondary-page');
      }

      if (!foundSection) {
        const resEdu = await fetch(`${apiBase}/homepage/cms/sections?page=education-program`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (resEdu.ok) {
          const jsonEdu = await resEdu.json();
          const itemsEdu = Array.isArray(jsonEdu.data) ? jsonEdu.data : Object.values(jsonEdu.data || {});
          foundSection = itemsEdu.find(s => s.section_type === 'secondary');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let rawContent = foundSection.content_published || foundSection.content;
        if (typeof rawContent === 'string') {
          try {
            rawContent = JSON.parse(rawContent);
          } catch (e) {
            rawContent = {};
          }
        }

        const dataEn = rawContent?.en || rawContent || {};
        const merged = {
          hero: { ...SECONDARY_DEFAULTS.hero, ...(dataEn.hero || {}) },
          hero_stats: Array.isArray(dataEn.hero_stats) && dataEn.hero_stats.length > 0 ? dataEn.hero_stats : SECONDARY_DEFAULTS.hero_stats,
          overview: { ...SECONDARY_DEFAULTS.overview, ...(dataEn.overview || {}) },
          program_highlights: Array.isArray(dataEn.program_highlights) && dataEn.program_highlights.length > 0 ? dataEn.program_highlights : SECONDARY_DEFAULTS.program_highlights,
          pathways: Array.isArray(dataEn.pathways) && dataEn.pathways.length > 0 ? dataEn.pathways : SECONDARY_DEFAULTS.pathways,
          subject_tracks: Array.isArray(dataEn.subject_tracks) && dataEn.subject_tracks.length > 0 ? dataEn.subject_tracks : SECONDARY_DEFAULTS.subject_tracks,
          support: { ...SECONDARY_DEFAULTS.support, ...(dataEn.support || {}) },
          achievements: { ...SECONDARY_DEFAULTS.achievements, ...(dataEn.achievements || {}) },
          opportunities: { ...SECONDARY_DEFAULTS.opportunities, ...(dataEn.opportunities || {}) },
          cta: { ...SECONDARY_DEFAULTS.cta, ...(dataEn.cta || {}) }
        };

        setFormData(merged);
        setOriginalData(merged);

        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = {};
              pendJson.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  pendMap[c.field_id] = true;
                }
              });
              setPendingFields(pendMap);
            }
          }
        } catch (pe) {
          console.warn('[SecondaryPageForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[SecondaryPageForm] Load error:', err);
      toast.error('Gagal memuat data Secondary Program');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const submitField = async (fieldId, fieldLabel, value, oldValue) => {
    if (!sectionId) {
      toast.error('Section ID belum terdaftar di database');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldLabel,
          old_value: oldValue ?? '',
          new_value: value ?? '',
          locale: 'en'
        })
      });

      const json = await res.json();
      if (json.success || json.ok) {
        toast.success(`Pengajuan ${fieldLabel} berhasil dikirim untuk review`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
      } else {
        toast.error(json.error || json.message || 'Gagal mengajukan perubahan');
      }
    } catch (e) {
      console.error('[SecondaryPageForm] Submit error:', e);
      toast.error('Terjadi kesalahan: ' + e.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleHeroChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const handleHeroStatChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.hero_stats];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, hero_stats: updated };
    });
  };

  const handleOverviewChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      overview: { ...prev.overview, [field]: value }
    }));
  };

  const handleHighlightChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.program_highlights];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, program_highlights: updated };
    });
  };

  const handlePathwayChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.pathways];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, pathways: updated };
    });
  };

  const handlePathwayFeaturesChange = (index, textValue) => {
    const list = textValue.split('\n').map(s => s.trim()).filter(Boolean);
    setFormData(prev => {
      const updated = [...prev.pathways];
      updated[index] = { ...updated[index], features: list, _rawFeatures: textValue };
      return { ...prev, pathways: updated };
    });
  };

  const handleTrackChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.subject_tracks];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, subject_tracks: updated };
    });
  };

  const handleTrackSubjectsChange = (index, textValue) => {
    const subjects = textValue.split(',').map(s => s.trim()).filter(Boolean);
    setFormData(prev => {
      const updated = [...prev.subject_tracks];
      updated[index] = { ...updated[index], subjects, _rawSubjects: textValue };
      return { ...prev, subject_tracks: updated };
    });
  };

  const handleSupportItemChange = (index, field, value) => {
    setFormData(prev => {
      const updatedItems = [...(prev.support?.items || [])];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      return {
        ...prev,
        support: { ...prev.support, items: updatedItems }
      };
    });
  };

  const handleAchievementsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      achievements: { ...prev.achievements, [field]: value }
    }));
  };

  const handleAchievementItemChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...(prev.achievements?.items || [])];
      updated[index] = { ...updated[index], [field]: value };
      return {
        ...prev,
        achievements: { ...prev.achievements, items: updated }
      };
    });
  };

  const handleOpportunitiesChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      opportunities: { ...prev.opportunities, [field]: value }
    }));
  };

  const handleOpportunityItemChange = (index, value) => {
    setFormData(prev => {
      const updated = [...(prev.opportunities?.items || [])];
      updated[index] = value;
      return {
        ...prev,
        opportunities: { ...prev.opportunities, items: updated }
      };
    });
  };

  const handleAddOpportunity = () => {
    setFormData(prev => ({
      ...prev,
      opportunities: {
        ...prev.opportunities,
        items: [...(prev.opportunities?.items || []), 'New Enrichment Opportunity']
      }
    }));
  };

  const handleRemoveOpportunity = (index) => {
    setFormData(prev => ({
      ...prev,
      opportunities: {
        ...prev.opportunities,
        items: (prev.opportunities?.items || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleCtaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      cta: { ...prev.cta, [field]: value }
    }));
  };

  const handleImageUpload = async (uploadKey, file, callback) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran gambar maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format gambar harus JPG, PNG, atau WEBP');
      return;
    }

    setImageUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('type', 'image');

      let token = localStorage.getItem('token') || localStorage.getItem('ph_auth_token') || sessionStorage.getItem('token');
      const res = await fetch(`${apiBase}/homepage/cms/media`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: data
      });

      const json = await res.json();
      if (json.ok && json.data) {
        const url = json.data.url || json.data.file_path;
        callback(url);
        toast.success('Foto berhasil diupload');
      } else {
        toast.error(json.error || 'Gagal upload foto');
      }
    } catch (e) {
      console.error('[SecondaryPageForm] Upload error:', e);
      toast.error('Gagal upload: ' + e.message);
    } finally {
      setImageUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-spinner" />
        <p>Memuat data Secondary Detail Page...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-wrapper">
      <ToastContainer />

      {/* Top Banner */}
      <div className="unified-header-card">
        <div className="header-info">
          <h2>Secondary Detail Page Management</h2>
          <p>Kelola seluruh konten detail halaman Secondary Program, kurikulum, jalur akademik, dan kegiatan siswa.</p>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="unified-tab-bar sub-tabs-bar">
        {SUB_TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-pill-btn ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} className="w-4 h-4 mr-2" />
              <span>{tab.title}</span>
            </button>
          );
        })}
      </div>

      {/* TAB 1: HERO & STATS */}
      {activeTab === 'hero' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <GraduationCap size={20} />
              <h3>Hero Banner & Key Statistics</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => submitField('hero', 'Secondary Hero', formData.hero, originalData.hero)}
              disabled={submitting.hero}
            >
              <Save size={16} />
              <span>{submitting.hero ? 'Menyimpan...' : 'Simpan Hero'}</span>
            </button>
          </div>
          <div className="card-body form-grid-2">
            <div className="form-group">
              <label>Grade Level Badge</label>
              <input
                type="text"
                value={formData.hero.badge}
                onChange={e => handleHeroChange('badge', e.target.value)}
                placeholder="Grades 7-12"
              />
            </div>
            <div className="form-group">
              <label>Heading</label>
              <input
                type="text"
                value={formData.hero.heading}
                onChange={e => handleHeroChange('heading', e.target.value)}
                placeholder="Secondary Program"
              />
            </div>
            <div className="form-group full-width">
              <label>Subtitle</label>
              <textarea
                rows={2}
                value={formData.hero.subtitle}
                onChange={e => handleHeroChange('subtitle', e.target.value)}
                placeholder="Preparing confident, capable leaders for university and beyond"
              />
            </div>
          </div>

          <div className="sub-section-divider">
            <h4>Hero Fast Stats (3 Point Sorotan)</h4>
            <button
              type="button"
              className="btn-field-save"
              onClick={() => submitField('hero_stats', 'Secondary Hero Stats', formData.hero_stats, originalData.hero_stats)}
              disabled={submitting.hero_stats}
            >
              <Save size={14} /> <span>{submitting.hero_stats ? 'Menyimpan...' : 'Simpan Stats'}</span>
            </button>
          </div>
          <div className="card-body form-grid-3">
            {formData.hero_stats.map((stat, idx) => (
              <div key={idx} className="item-sub-card">
                <span className="item-counter">Stat #{idx + 1}</span>
                <div className="form-group">
                  <label>Value / Number</label>
                  <input
                    type="text"
                    value={stat.number}
                    onChange={e => handleHeroStatChange(idx, 'number', e.target.value)}
                    placeholder="25 / LEAPS"
                  />
                </div>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    type="text"
                    value={stat.label}
                    onChange={e => handleHeroStatChange(idx, 'label', e.target.value)}
                    placeholder="Maximum Class"
                  />
                </div>
                <div className="form-group">
                  <label>Sublabel (Opsional)</label>
                  <input
                    type="text"
                    value={stat.sublabel || ''}
                    onChange={e => handleHeroStatChange(idx, 'sublabel', e.target.value)}
                    placeholder="Cambridge + National"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: OVERVIEW & HIGHLIGHTS */}
      {activeTab === 'overview' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <Layers size={20} />
              <h3>Program Overview & 4 Highlights</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => {
                submitField('overview', 'Secondary Overview', formData.overview, originalData.overview);
                submitField('program_highlights', 'Program Highlights', formData.program_highlights, originalData.program_highlights);
              }}
              disabled={submitting.overview || submitting.program_highlights}
            >
              <Save size={16} />
              <span>Simpan Overview & Highlights</span>
            </button>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>Overview Heading</label>
              <input
                type="text"
                value={formData.overview.heading}
                onChange={e => handleOverviewChange('heading', e.target.value)}
                placeholder="Secondary Excellence"
              />
            </div>
            <div className="form-group">
              <label>Overview Full Narrative</label>
              <textarea
                rows={4}
                value={formData.overview.description}
                onChange={e => handleOverviewChange('description', e.target.value)}
                placeholder="Penjelasan komprehensif keunggulan Secondary Program..."
              />
            </div>
          </div>

          <div className="sub-section-divider">
            <h4>4 Pilar Program Highlights</h4>
          </div>
          <div className="card-body form-grid-2">
            {formData.program_highlights.map((item, idx) => (
              <div key={idx} className="item-sub-card">
                <span className="item-counter">Pilar #{idx + 1}</span>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={e => handleHighlightChange(idx, 'title', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={item.description}
                    onChange={e => handleHighlightChange(idx, 'description', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: ACADEMIC PATHWAYS */}
      {activeTab === 'pathways' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <BookOpen size={20} />
              <h3>Academic Pathways (3 Level Pendidikan)</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => submitField('pathways', 'Academic Pathways', formData.pathways, originalData.pathways)}
              disabled={submitting.pathways}
            >
              <Save size={16} />
              <span>Simpan Pathways</span>
            </button>
          </div>
          <div className="card-body">
            {formData.pathways.map((pathway, idx) => (
              <div key={idx} className="pathway-detail-card" style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: '#1e293b' }}>{pathway.level}</h4>
                  <span className="badge-tag">Level #{idx + 1}</span>
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Level Name</label>
                    <input
                      type="text"
                      value={pathway.level}
                      onChange={e => handlePathwayChange(idx, 'level', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Title Focus</label>
                    <input
                      type="text"
                      value={pathway.title}
                      onChange={e => handlePathwayChange(idx, 'title', e.target.value)}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      rows={2}
                      value={pathway.description}
                      onChange={e => handlePathwayChange(idx, 'description', e.target.value)}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Features (1 baris per poin fitur kurikulum)</label>
                    <textarea
                      rows={4}
                      value={pathway._rawFeatures ?? (pathway.features || []).join('\n')}
                      onChange={e => handlePathwayFeaturesChange(idx, e.target.value)}
                      placeholder="Balanced curriculum&#10;Study skills and self-management&#10;Character and habits building"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: SUBJECT TRACKS */}
      {activeTab === 'tracks' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <Compass size={20} />
              <h3>Subject Tracks & Career Pathways</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => submitField('subject_tracks', 'Subject Tracks', formData.subject_tracks, originalData.subject_tracks)}
              disabled={submitting.subject_tracks}
            >
              <Save size={16} />
              <span>Simpan Tracks</span>
            </button>
          </div>
          <div className="card-body form-grid-2">
            {formData.subject_tracks.map((track, idx) => (
              <div key={idx} className="item-sub-card">
                <span className="item-counter">Track #{idx + 1}</span>
                <div className="form-group">
                  <label>Track Name</label>
                  <input
                    type="text"
                    value={track.track}
                    onChange={e => handleTrackChange(idx, 'track', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Mata Pelajaran Kunci (pisahkan koma)</label>
                  <input
                    type="text"
                    value={track._rawSubjects ?? (Array.isArray(track.subjects) ? track.subjects.join(', ') : track.subjects || '')}
                    onChange={e => handleTrackSubjectsChange(idx, e.target.value)}
                    placeholder="Biology, Chemistry, Physics"
                  />
                </div>
                <div className="form-group">
                  <label>Peluang Karir Terkait</label>
                  <textarea
                    rows={2}
                    value={track.careers}
                    onChange={e => handleTrackChange(idx, 'careers', e.target.value)}
                    placeholder="Medicine, Pharmacy, Biotechnology..."
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 5: SUPPORT & COUNSELING */}
      {activeTab === 'support' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <Sparkles size={20} />
              <h3>Guidance & Counseling Support</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => submitField('support', 'Student Support', formData.support, originalData.support)}
              disabled={submitting.support}
            >
              <Save size={16} />
              <span>Simpan Support</span>
            </button>
          </div>
          <div className="card-body form-grid-2">
            <div className="form-group">
              <label>Section Heading</label>
              <input
                type="text"
                value={formData.support?.heading || ''}
                onChange={e => setFormData(p => ({ ...p, support: { ...p.support, heading: e.target.value } }))}
              />
            </div>
            <div className="form-group">
              <label>Section Description</label>
              <input
                type="text"
                value={formData.support?.description || ''}
                onChange={e => setFormData(p => ({ ...p, support: { ...p.support, description: e.target.value } }))}
              />
            </div>
          </div>
          <div className="card-body form-grid-2">
            {(formData.support?.items || []).map((item, idx) => (
              <div key={idx} className="item-sub-card">
                <span className="item-counter">Layanan #{idx + 1}</span>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={e => handleSupportItemChange(idx, 'title', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    rows={2}
                    value={item.description}
                    onChange={e => handleSupportItemChange(idx, 'description', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 6: ACHIEVEMENTS & OPPORTUNITIES */}
      {activeTab === 'achievements' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <Award size={20} />
              <h3>Student Achievements & Leadership Opportunities</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => {
                submitField('achievements', 'Student Achievements', formData.achievements, originalData.achievements);
                submitField('opportunities', 'Opportunities & Leadership', formData.opportunities, originalData.opportunities);
              }}
              disabled={submitting.achievements || submitting.opportunities}
            >
              <Save size={16} />
              <span>Simpan Prestasi & Peluang</span>
            </button>
          </div>
          
          <div className="sub-section-divider">
            <h4>Statistik Prestasi Siswa</h4>
          </div>
          <div className="card-body form-grid-4">
            {(formData.achievements?.items || []).map((item, idx) => (
              <div key={idx} className="item-sub-card">
                <div className="form-group">
                  <label>Angka / Persentase</label>
                  <input
                    type="text"
                    value={item.number}
                    onChange={e => handleAchievementItemChange(idx, 'number', e.target.value)}
                    placeholder="95%"
                  />
                </div>
                <div className="form-group">
                  <label>Label</label>
                  <input
                    type="text"
                    value={item.label}
                    onChange={e => handleAchievementItemChange(idx, 'label', e.target.value)}
                    placeholder="University Acceptance"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="sub-section-divider" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>Kegiatan Pengembangan & Enrichment</h4>
            <button type="button" className="btn-add-item" onClick={handleAddOpportunity}>
              <Plus size={14} /> Tambah Peluang
            </button>
          </div>
          <div className="card-body form-grid-2">
            {(formData.opportunities?.items || []).map((opp, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  value={opp}
                  onChange={e => handleOpportunityItemChange(idx, e.target.value)}
                />
                <button
                  type="button"
                  className="btn-item-delete"
                  onClick={() => handleRemoveOpportunity(idx)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 7: CTA */}
      {activeTab === 'cta' && (
        <div className="unified-section-card">
          <div className="card-header">
            <div className="title-with-icon">
              <PhoneCall size={20} />
              <h3>Call to Action (CTA)</h3>
            </div>
            <button
              type="button"
              className="unified-btn-primary"
              onClick={() => submitField('cta', 'Secondary CTA', formData.cta, originalData.cta)}
              disabled={submitting.cta}
            >
              <Save size={16} />
              <span>Simpan CTA</span>
            </button>
          </div>
          <div className="card-body form-grid-2">
            <div className="form-group full-width">
              <label>Heading</label>
              <input
                type="text"
                value={formData.cta?.heading || ''}
                onChange={e => handleCtaChange('heading', e.target.value)}
                placeholder="Ready to Begin Your Secondary Journey?"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                rows={2}
                value={formData.cta?.description || ''}
                onChange={e => handleCtaChange('description', e.target.value)}
                placeholder="Join a community of ambitious learners preparing for global success"
              />
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Preview */}
      {lightboxImage && (
        <div className="unified-lightbox-backdrop" onClick={() => setLightboxImage(null)}>
          <div className="unified-lightbox-modal" onClick={e => e.stopPropagation()}>
            <button type="button" className="unified-lightbox-close" onClick={() => setLightboxImage(null)}>
              <X size={20} />
            </button>
            <img src={lightboxImage} alt="Preview" className="unified-lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
}
